﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import { NotificationType, sendNotification } from '@tk/utilities/tk.notification';
import KDMZLoadingAnimation from '@/components/KDMZ.loading.animation';

interface PostFieldResponse {
    success: boolean;
    message: string;
}

export default class KDMZAsyncPostField extends TKCustomElementFactory {
    action?: string;
    submitButton?: HTMLButtonElement;

    fieldElement?: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    fieldCheckbox?: HTMLInputElement;
    fieldRange?: HTMLInputElement;
    fieldRadio?: HTMLInputElement;

    timer?: ReturnType<typeof setTimeout>;
    isChecked?: boolean;

    reload: boolean;
    initOnLoad: boolean;
    hideMessage: boolean;

    hasFormAsParent: boolean;
    formular?: HTMLFormElement;
    submitButtons?: HTMLButtonElement[];

    constructor() {
        super();

        this.action = this.getAttribute('data-tk-action') || undefined;
        this.submitButton = this.querySelector('button[type="submit"]') || undefined;

        // Generisches Feld
        this.fieldElement = this.querySelector('input, select, textarea') || undefined;

        // Typ-spezifische Referenzen
        this.fieldCheckbox = this.querySelector('input[type="checkbox"]') || undefined;
        this.fieldRange = this.querySelector('input[type="range"]') || undefined;
        this.fieldRadio = this.querySelector('input[type="radio"]') || undefined;

        this.isChecked = false;

        this.reload = this.getAttribute('data-tk-reload') === 'true';
        this.initOnLoad = this.hasAttribute('data-tk-action-oninit');
        this.hideMessage = this.hasAttribute('data-tk-message-hide');

        this.hasFormAsParent = !!this.closest('form');
        this.formular = this.closest('form') || undefined;
        this.submitButtons = this.hasFormAsParent
            ? Array.from(this.formular?.querySelectorAll('button[type="submit"]') || [])
            : undefined;
    }

    connectedCallback(): void {
        if (this.action === undefined || !this.fieldElement) throw new Error('Async Post Field: Elements are missing!');

        if (this.initOnLoad) {
            this.post();
        }

        this.submitButton ? this.registerSubmitListener() : this.registerInputListener();
    }

    registerInputListener() {
        // Feld-Typ identifizieren
        if (this.fieldElement?.type === 'checkbox') {
            this.fieldElement = this.fieldCheckbox;
        } else if (this.fieldElement?.type === 'range') {
            this.fieldElement = this.fieldRange;
        } else if (this.fieldElement?.type === 'radio') {
            this.fieldElement = this.fieldRadio;
        }

        if (this.fieldElement?.type === 'radio') {
            const radios = this.querySelectorAll<HTMLInputElement>(
                `input[type="radio"][name="${this.fieldElement.name}"]`,
            );
            radios.forEach((radio) => {
                this.pushListener({
                    event: 'change',
                    element: radio,
                    action: this.handleInput.bind(this),
                });
            });
        } else {
            this.pushListener({
                event: 'change',
                element: this.fieldElement!,
                action: this.handleInput.bind(this),
            });
        }

        this.pushListener({
            event: 'keydown',
            element: this,
            action: this.handleKeydown.bind(this),
        });
    }

    registerSubmitListener() {
        this.pushListener({
            event: 'click',
            element: this.submitButton!,
            action: this.post.bind(this),
        });
    }

    handleInput() {
        // Checkbox
        if (this.fieldElement?.type === 'checkbox') {
            this.fieldElement = this.fieldCheckbox;
            this.isChecked = this.fieldElement?.checked;
        }

        // Range
        else if (this.fieldElement?.type === 'range') {
            this.fieldElement = this.fieldRange;
        }

        // Radio → immer aktuell ausgewähltes Radio ermitteln
        else if (this.fieldElement?.type === 'radio') {
            const name = this.fieldElement.name;
            const checked = this.querySelector<HTMLInputElement>(`input[type="radio"][name="${name}"]:checked`);
            if (checked) this.fieldElement = checked;
        }

        // Delay für asynchronen Request
        if (this.timer) clearTimeout(this.timer);
        this.timer = setTimeout(() => this.post(), 500);
    }

    post() {
        // Wert ermitteln
        const value = this.fieldElement?.value ?? '';
        const payload: { value: string } = { value };

        // ------------------------------------------------------
        //  RADIO-GRUPPEN FIX (GARANTIERT KORREKT)
        // ------------------------------------------------------
        if (this.fieldRadio) {
            const name = this.fieldRadio.name;

            // Immer das tatsächlich angeklickte Radio suchen
            const checked = this.querySelector<HTMLInputElement>(`input[type="radio"][name="${name}"]:checked`);

            if (checked) {
                payload.value = checked.value;
            } else {
                payload.value = '';
            }
        }
        // ------------------------------------------------------

        // Komplette Gruppen (combined)
        if (this.fieldElement?.closest('.kdmz-async-post-field-combined')) {
            let temp = '';
            const groupWrapper = this.fieldElement.closest('.kdmz-async-post-field-combined');

            groupWrapper
                ?.querySelectorAll<
                    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                >('input, select, textarea')
                .forEach((field) => {
                    temp = temp + ' ' + field.value;
                });

            payload.value = temp.trim();
            this.action = groupWrapper.getAttribute('data-tk-action') || undefined;
        }

        // Checkbox normalisieren
        if (this.fieldElement?.type === 'checkbox') {
            payload.value = this.isChecked ? '1' : '0';
        }

        KDMZLoadingAnimation.createLoadingScreen();

        let i = 0;
        payload.value = payload.value.replace(/"/g, () => {
            return i++ % 2 === 0 ? '“' : '”';
        });

        fetchRequest({
            requestURL: this.action!,
            payload,
            resolveHandler: KDMZAsyncPostField.handleResponse.bind(this),
        });
    }

    static handleResponse(response: TKResponse<PostFieldResponse>) {
        this.reactivateDisabledSubmitButtons();

        if (!response || !response.success) return;

        const { success, message } = response.dataAsJson;
        if (message && !this.hideMessage) {
            success
                ? sendNotification(NotificationType.SUCCESS, message)
                : sendNotification(NotificationType.ERROR, message);
        }

        if (this.reload) location.reload();

        KDMZLoadingAnimation.destroyLoadingScreen();
    }

    handleKeydown(event: KeyboardEvent) {
        if (this.hasFormAsParent) {
            this.submitButtons?.forEach((btn) => (btn.disabled = true));
        }
    }

    reactivateDisabledSubmitButtons() {
        if (this.hasFormAsParent) {
            this.submitButtons?.forEach((btn) => (btn.disabled = false));
        }
    }
}
